import { type CheckedState } from '@radix-ui/react-checkbox'
import { create } from 'zustand'

import { type ClientPortalOption } from '../queries/case-dashboard/types'
import { ALL_APPLICANT_CASES, type ApplicantCaseItem } from './client-portal/mock'

export interface ClientPortalInternalSampleStoreData {
  currentCase: ApplicantCaseItem
  allApplicantCases: ApplicantCaseItem[]
  selectedCases: {
    caseId: number
    caseType: string
    caseNumber: string
  }[]
  goToCase: (caseId: number) => void
  goToPreviousCase: VoidFunction
  goToNextCase: VoidFunction
  updateCheckedCase: (caseId: number, checked: CheckedState) => void
  updatePortalOption: (portalOption: ClientPortalOption) => void
}

export const useClientPortalInternalSampleStore = create<ClientPortalInternalSampleStoreData>((set) => ({
  currentCase: ALL_APPLICANT_CASES[0],
  allApplicantCases: ALL_APPLICANT_CASES,
  selectedCases: ALL_APPLICANT_CASES.slice(0, 3),
  goToCase: (caseId) =>
    set((state) => {
      const newCurrentCase = state.allApplicantCases.find(({ caseId: _caseId }) => _caseId === caseId)

      if (!newCurrentCase) {
        return state
      }

      return { ...state, currentCase: newCurrentCase }
    }),
  updateCheckedCase: (caseId, checked) =>
    set((state) => {
      const targetCase = state.allApplicantCases.find(({ caseId: _caseId }) => _caseId === caseId)

      if (!targetCase) {
        return state
      }

      return {
        ...state,
        selectedCases: checked
          ? [...state.selectedCases, targetCase]
          : state.selectedCases.filter(({ caseId: _caseId }) => _caseId !== targetCase?.caseId),
      }
    }),
  goToPreviousCase: () =>
    set((state) => {
      const currentCaseIdIndex = state.allApplicantCases.findIndex(
        ({ caseId: _caseId }) => _caseId === state.currentCase.caseId
      )

      if (currentCaseIdIndex === 0) {
        return state
      }

      const previousCase = state.allApplicantCases[currentCaseIdIndex - 1]
      return {
        ...state,
        currentCase: previousCase,
      }
    }),
  goToNextCase: () =>
    set((state) => {
      const currentCaseIdIndex = state.allApplicantCases.findIndex(
        ({ caseId: _caseId }) => _caseId === state.currentCase.caseId
      )

      if (currentCaseIdIndex === state.allApplicantCases.length - 1) {
        return state
      }

      const nextCase = state.allApplicantCases[currentCaseIdIndex + 1]
      return {
        ...state,
        currentCase: nextCase,
      }
    }),
  updatePortalOption: (portalOption) =>
    set((state) => {
      if (portalOption.isDefaultSetting) {
        return {
          ...state,
          currentCase: {
            ...state.currentCase,
            portalOption,
          },
          allApplicantCases: state.allApplicantCases.map((caseItem) => ({
            ...caseItem,
            portalOption,
          })),
        }
      }

      const targetUpdatedAllApplicantCases = state.allApplicantCases.map((caseItem) => {
        if (caseItem.caseId !== portalOption.caseId) {
          return {
            ...caseItem,
            portalOption: {
              ...caseItem.portalOption,
              isDefaultSetting: false,
            },
          }
        }

        return {
          ...caseItem,
          portalOption: {
            ...portalOption,
            isDefaultSetting: false,
          },
        }
      })

      return {
        ...state,
        allApplicantCases: targetUpdatedAllApplicantCases,
        currentCase: {
          ...state.currentCase,
          portalOption,
        },
      }
    }),
}))
